import type { SearchResult } from '@/types';
import { getCategoryIcon, getCategoryLabel } from '@/utils/categories';

interface SearchSuggestionsProps {
  suggestions: SearchResult[];
  recentSearches: SearchResult[];
  // Trending-near-you places from the ranking engine. Only shown when the box is
  // empty (no live suggestions). Empty array => the section is simply not shown;
  // trending data is never fabricated.
  trending?: SearchResult[];
  // Present when the query parses as a raw coordinate — rendered as a distinct
  // "Go to …" action at the top. Needs no backend, so it always works.
  coordinateResult?: SearchResult | null;
  // True when the name-search backend (geocoding) is down/timed out for the
  // current query. Surfaces a clear, non-blocking notice instead of nothing.
  searchUnavailable?: boolean;
  onSelect: (result: SearchResult) => void;
  onSelectCoordinate?: (result: SearchResult) => void;
  // Recent-history interactions. When omitted, a recent row falls back to the
  // normal onSelect and the remove/clear affordances are hidden.
  onSelectRecent?: (result: SearchResult) => void;
  onRemoveRecent?: (id: string) => void;
  onClearRecents?: () => void;
}

export function SearchSuggestions({
  suggestions,
  recentSearches,
  trending = [],
  coordinateResult = null,
  searchUnavailable = false,
  onSelect,
  onSelectCoordinate,
  onSelectRecent,
  onRemoveRecent,
  onClearRecents,
}: SearchSuggestionsProps) {
  // Trending + recent are "empty box" content, only relevant when there are no
  // live autocomplete suggestions.
  const showTrending = suggestions.length === 0 && trending.length > 0;
  // The unavailable notice only makes sense when we have nothing better to show
  // (no coordinate action, no live suggestions).
  const showUnavailable =
    searchUnavailable && !coordinateResult && suggestions.length === 0;
  const hasContent =
    !!coordinateResult ||
    showUnavailable ||
    suggestions.length > 0 ||
    recentSearches.length > 0 ||
    showTrending;

  if (!hasContent) {
    return null;
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-dropdown overflow-hidden z-dropdown animate-fade-in">
      {/* Go to coordinates — distinct top action, no backend required */}
      {coordinateResult && (
        <button
          onClick={() => (onSelectCoordinate ?? onSelect)(coordinateResult)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-gray-100 hover:bg-primary-50 transition-colors"
        >
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-50 text-primary-600">
            <PinIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              Go to {coordinateResult.name}
            </div>
            <div className="text-sm text-gray-500">Coordinates</div>
          </div>
        </button>
      )}

      {/* Name search temporarily unavailable — guide the user to coordinates */}
      {showUnavailable && (
        <div className="flex items-start gap-2 px-4 py-3 text-sm text-gray-600 bg-warning-50">
          <WarnIcon className="w-5 h-5 flex-shrink-0 text-warning-500 mt-0.5" />
          <span>
            Place search is temporarily unavailable — try coordinates like{' '}
            <span className="font-medium text-gray-800">12.905, 77.595</span>
          </span>
        </div>
      )}

      {/* Trending near you */}
      {showTrending && (
        <div>
          <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50 flex items-center gap-1.5">
            <TrendingIcon className="w-3.5 h-3.5 text-primary-600" />
            Trending near you
          </div>
          <div className="flex flex-wrap gap-2 px-4 py-3">
            {trending.map((result) => (
              <button
                key={result.id}
                onClick={() => onSelect(result)}
                className="max-w-full inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-primary-50 hover:text-primary-700 text-sm text-gray-700 transition-colors"
                title={result.name || result.displayName}
              >
                <TrendingIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary-500" />
                <span className="truncate">{result.name || result.displayName}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent searches — locally persisted, most-recent-first. Each row has a
          per-item remove; the header has a clear-all. */}
      {recentSearches.length > 0 && suggestions.length === 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Recent
            </span>
            {onClearRecents && (
              <button
                onClick={onClearRecents}
                className="text-xs font-medium text-gray-400 hover:text-primary-600 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          <ul>
            {recentSearches.slice(0, 8).map((result) => (
              <SuggestionItem
                key={result.id}
                result={result}
                onSelect={onSelectRecent ?? onSelect}
                onRemove={onRemoveRecent}
                icon={<HistoryIcon className="w-5 h-5 text-gray-400" />}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <ul>
          {suggestions.map((result) => (
            <SuggestionItem
              key={result.id}
              result={result}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface SuggestionItemProps {
  result: SearchResult;
  onSelect: (result: SearchResult) => void;
  // When provided, renders a per-item remove button (used by Recent history).
  onRemove?: (id: string) => void;
  icon?: React.ReactNode;
}

function SuggestionItem({ result, onSelect, onRemove, icon }: SuggestionItemProps) {
  const categoryIcon = icon ?? getCategoryIcon(result.category);
  const categoryLabel = getCategoryLabel(result.category);

  return (
    <li className="group relative flex items-stretch hover:bg-gray-50 transition-colors">
      <button
        onClick={() => onSelect(result)}
        className="flex-1 min-w-0 flex items-start gap-3 px-4 py-3 text-left"
      >
        <div className="flex-shrink-0 mt-0.5">
          {typeof categoryIcon === 'string' ? (
            <span className="text-lg">{categoryIcon}</span>
          ) : (
            categoryIcon
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">{result.name || result.displayName}</div>
          <div className="text-sm text-gray-500 truncate">
            {categoryLabel && (
              <span className="text-gray-400 mr-1">{categoryLabel} &middot;</span>
            )}
            {result.address.formatted}
          </div>
          {result.distance !== undefined && (
            <div className="text-xs text-gray-400 mt-0.5">
              {formatDistance(result.distance)}
            </div>
          )}
        </div>
      </button>
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${result.name || result.displayName} from recent searches`}
          onClick={(e) => {
            // Don't trigger the row's select handler.
            e.stopPropagation();
            onRemove(result.id);
          }}
          className="flex-shrink-0 flex items-center px-3 text-gray-300 hover:text-gray-600 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      )}
    </li>
  );
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m away`;
  }
  return `${(meters / 1000).toFixed(1)} km away`;
}

function TrendingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
      />
    </svg>
  );
}

function HistoryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function WarnIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z"
      />
    </svg>
  );
}
